import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ThermometerIcon, DropletsIcon, ZapIcon, ActivityIcon, LightbulbIcon,
  ToggleLeftIcon, GaugeIcon, WindIcon, CloudIcon, ChevronLeftIcon, ChevronRightIcon,
  PersonStandingIcon, RadarIcon, HeartPulseIcon, ShieldCheckIcon, LockIcon, Link2Icon
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, LineChart, Line } from 'recharts';

import { useTelemetry } from '../hooks/useTelemetry';
import { useTelemetryHistory } from '../hooks/useHistoricalTelemetry';
import { tbToChartData, tbToCombinedChartData } from '../utils/tbChartHelper';
import { thingsboardService } from '../services/thingsboard';
import { useTranslation } from 'react-i18next';

import {
  TimeRange, Mode, SELMode, SEL_MODES, MODES, SensorDetailSidebarProps,
  TimeRangeTabs, ChartSection, PasswordToast, ToggleSwitch, SensorHeader, SwitchConnectPopup
} from './SensorDetailShared';

// ─── SEL Detail View ───
export function SELDetail({ sensor, onBack, onClose, onUpdateSensor }: SensorDetailSidebarProps) {
  const { t } = useTranslation();
  const deviceId = sensor.id;
  const { data: realtimeData } = useTelemetry(deviceId, ['power_instant', 'temperature', 'humidity', 'pressure', 'light_state','presence_state']);

  const [powerRange, setPowerRange] = useState<TimeRange>(sensor.active ? 'realtime' : 'monthly');
  const [tempHumidityRange, setTempHumidityRange] = useState<TimeRange>(sensor.active ? 'realtime' : 'monthly');
  const [pressureRange, setPressureRange] = useState<TimeRange>(sensor.active ? 'realtime' : 'monthly');

  const powerKey = powerRange === 'realtime' ? 'power_instant' :
                   powerRange === 'daily' ? 'hourly_power' :
                   powerRange === 'weekly' ? 'daily_power' : 'daily_power';

  const { data: histPower } = useTelemetryHistory(deviceId, [powerKey], powerRange);
  const { data: histTempHumid } = useTelemetryHistory(deviceId, ['temperature', 'humidity'], tempHumidityRange);
  const { data: histPressure } = useTelemetryHistory(deviceId, ['pressure'], pressureRange);
 
  const [isLightOn, setIsLightOn] = useState(true);
  const [isOccupied, setIsOccupied] = useState(true);
  const [selMode, setSelMode] = useState<SELMode>('normal');

  const [linkedSes, setLinkedSes] = useState<{ id: string; name: string } | null>(null);
  const [showLinkPopup, setShowLinkPopup] = useState(false);
  const [isSwitchOn, setIsSwitchOn] = useState(false);

  // Fetch initial linked SES on mount or sensor change
  useEffect(() => {
    async function fetchLinkedSes() {
      try {
        const rels = await thingsboardService.getRelations('DEVICE', deviceId);
        const operateRel = rels.find((r: any) => r.type === 'operate' && r.to?.entityType === 'DEVICE');
        if (operateRel) {
          setLinkedSes({ id: operateRel.to.id, name: operateRel.toName || 'SES Switch' });
        } else {
          setLinkedSes(null);
        }
      } catch (err) {
        console.error('Failed to fetch linked SES:', err);
      }
    }
    fetchLinkedSes();
  }, [deviceId]);

  // Subscribe to linked SES switch telemetry
  const { data: sesRealtimeData } = useTelemetry(linkedSes?.id || '', ['switch']);

  // Sync switch state from linked SES telemetry (or fallback to SEL light_state)
  useEffect(() => {
    if (linkedSes?.id) {
      const switchState = sesRealtimeData?.['switch'];
      if (switchState && switchState.length > 0) {
        const val = switchState[0].value;
        setIsSwitchOn(String(val) === 'on' || String(val) === 'true');
      }
    } else {
      const lightVal = realtimeData['light_state']?.[0]?.value;
      if (lightVal !== undefined) setIsSwitchOn(lightVal === 'true' || lightVal === 'on' || lightVal === '0');
    }
  }, [sesRealtimeData, linkedSes, realtimeData]);

  const handleToggleSwitch = async () => {
    if (!linkedSes?.id) {
      // If no switch linked, prompt the user to link one
      setShowLinkPopup(true);
      return;
    }
    const newValue = !isSwitchOn;
    setIsSwitchOn(newValue); // Optimistic UI update
    try {
      await thingsboardService.sendOneWayRpc(linkedSes.id, 'set_switch', { value: newValue });
    } catch (error) {
      console.error('Failed to send RPC command to linked SES switch:', error);
      setIsSwitchOn(!newValue); // Revert on failure
    }
    setTimeout(() => {
      try {
        thingsboardService.sendOneWayRpc(linkedSes.id, 'set_switch', { value: newValue });
      } catch (error) {
        console.error('Failed to send RPC command to linked SES switch:', error);
      }
    }, 100);
  };

  // Sync UI state with ThingsBoard telemetry
  useEffect(() => {
    const lightVal = realtimeData['light_state']?.[0]?.value;
    const presenceVal = realtimeData['presence_state']?.[0]?.value;
    if (lightVal !== undefined) setIsLightOn(lightVal === 'true' || lightVal === '0');
    if (presenceVal !== undefined) setIsOccupied(presenceVal === 'true' || presenceVal === '0');
  }, [realtimeData]);
  const currentSelModeIndex = SEL_MODES.findIndex((m) => m.id === selMode);
  const navigateSelMode = (direction: -1 | 1) => {
    const nextIndex =
    (currentSelModeIndex + direction + SEL_MODES.length) % SEL_MODES.length;
    setSelMode(SEL_MODES[nextIndex].id);
  };

  const finalPowerData = useMemo(() => {
    return powerRange === 'realtime'
      ? tbToChartData(realtimeData, 'power_instant', 'realtime', [])
      : tbToChartData(histPower, powerKey, powerRange, [], 0.001);
  }, [powerRange, realtimeData, histPower]);

  const finalPressureData = useMemo(() => {
    return pressureRange === 'realtime'
      ? tbToChartData(realtimeData, 'pressure', 'realtime', [],0.01)
      : tbToChartData(histPressure, 'pressure', pressureRange, [], 0.01);
  }, [pressureRange, realtimeData, histPressure]);

  const finalTempHumidData = useMemo(() => {
    return tempHumidityRange === 'realtime'
      ? tbToCombinedChartData(realtimeData, 'temperature', 'temp', 'humidity', 'humidity', 'realtime', [])
      : tbToCombinedChartData(histTempHumid, 'temperature', 'temp', 'humidity', 'humidity', tempHumidityRange, []);
  }, [tempHumidityRange, realtimeData, histTempHumid]);

  return (
    <div className="w-full flex flex-col gap-2 p-3 border-l border-white/20 bg-[#0a0f1a]/30 h-full overflow-hidden">
      <SensorHeader
        sensor={sensor}
        subtitle="Smart Edge Light"
        onBack={onBack}
        onClose={onClose}
        onUpdateSensor={onUpdateSensor}
        extraAction={
          <button
            onClick={() => setShowLinkPopup(true)}
            className={`p-1 rounded-md transition-colors flex-shrink-0 ${
              linkedSes
                ? 'text-cyan-400 hover:text-cyan-300 bg-cyan-500/15 border border-cyan-500/30'
                : 'text-slate-500 hover:text-cyan-400 hover:bg-white/10'
            }`}
            title={linkedSes ? `연결된 스위치: ${linkedSes.name}` : t('sensor.connect_switch', '스위치 연결')}
          >
            <Link2Icon className="w-3 h-3" />
          </button>
        }
        rightSlot={
          <ToggleSwitch isOn={isSwitchOn} onToggle={handleToggleSwitch} />
        }
      />


      {/* SEL Mode Selection - Spin Box */}
      <motion.div
        initial={{
          opacity: 0,
          y: 10
        }}
        animate={{
          opacity: 1,
          y: 0
        }}
        className="glass-panel rounded-xl p-2.5 flex-shrink-0">

        <div className="flex items-center gap-2">
          {/* Spin Box - half width */}
          <div className="flex items-center w-2/3 bg-white/5 rounded-lg border border-white/10 overflow-hidden">
            <button
              onClick={() => navigateSelMode(-1)}
              className="p-2.5 bg-cyan-500/10 hover:bg-cyan-500/25 transition-all text-cyan-400 hover:text-cyan-300 flex-shrink-0 border-r border-white/10 active:scale-90">

              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <div className="flex-1 text-center py-1.5 relative overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={selMode}
                  initial={{
                    opacity: 0,
                    y: 10
                  }}
                  animate={{
                    opacity: 1,
                    y: 0
                  }}
                  exit={{
                    opacity: 0,
                    y: -10
                  }}
                  transition={{
                    duration: 0.15
                  }}
                  className="text-sm font-bold text-cyan-400">

                  {SEL_MODES[currentSelModeIndex]?.label}
                </motion.div>
              </AnimatePresence>
            </div>
            <button
              onClick={() => navigateSelMode(1)}
              className="p-2.5 bg-cyan-500/10 hover:bg-cyan-500/25 transition-all text-cyan-400 hover:text-cyan-300 flex-shrink-0 border-l border-white/10 active:scale-90">

              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Light + Presence icons - right aligned */}
          <div className="flex-1 flex items-center justify-end gap-2">
            <div
              className={`flex flex-col items-center gap-1 p-1.5 rounded-xl ${isLightOn ? 'bg-amber-500/20 text-amber-400' : 'bg-white/5 text-slate-600'}`}
              title={t('sensor.light_status')}>

              <LightbulbIcon
                className="w-7 h-7"
                fill={isLightOn ? 'currentColor' : 'none'} />

            </div>
            <div
              className={`flex flex-col items-center gap-1 p-1.5 rounded-xl ${isOccupied ? 'bg-cyan-500/20 text-cyan-400' : 'bg-white/5 text-slate-600'}`}
              title={t('sensor.presence_status')}>

              <PersonStandingIcon
                className="w-7 h-7"
                fill={isOccupied ? 'currentColor' : 'none'} />

            </div>
          </div>
        </div>
      </motion.div>

      <ChartSection
        icon={ZapIcon}
        iconColor="text-amber-400"
        title={t('sensor.energy_usage')}
        data={finalPowerData}
        timeRange={powerRange}
        onTimeRangeChange={setPowerRange}
        disabledRanges={!sensor.active ? ['realtime'] : []}
        chartType="area"
        color="#fbbf24"
        gradientId="selPower"
        className="flex-1 min-h-0"
        unit={powerRange === 'realtime' ? 'W' : 'kWh'}
        stats={[
        {
          label: powerRange === 'realtime' ? t('sensor.current_power_usage') : 
                 powerRange === 'daily' ? t('sensor.today_total_usage') :
                 powerRange === 'weekly' ? t('sensor.this_week_total_usage') : t('sensor.this_month_total_usage'),
          value: powerRange === 'realtime' 
            ? `${Number(realtimeData?.['power_instant']?.[0]?.value || 0).toFixed(1)} W`
            : `${finalPowerData.length > 0 ? finalPowerData.reduce((sum, item) => sum + (Number(item.value) || 0), 0).toFixed(2) : '0.00'} kWh`,
          color: 'text-violet-400'
        },
        {
          label: t('sensor.daily_average'),
          value: (() => {
            const validData = finalPowerData.filter(item => item.value !== null && item.value !== undefined && item.value !== '');
            if (validData.length === 0) return powerRange === 'realtime' ? '0.0 W' : '0.00 kWh';
            const sum = validData.reduce((acc, item) => acc + (Number(item.value) || 0), 0);
            const avg = sum / validData.length;
            return powerRange === 'realtime' ? `${avg.toFixed(1)} W` : `${avg.toFixed(2)} kWh`;
          })()
        }]
        } />


      {/* Combined Temperature + Humidity Card */}
      <motion.div
        initial={{
          opacity: 0,
          y: 10
        }}
        animate={{
          opacity: 1,
          y: 0
        }}
        className="glass-panel rounded-xl p-3 flex-1 min-h-0 flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between mb-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            <ThermometerIcon className="w-3.5 h-3.5 text-rose-400" />
            <DropletsIcon className="w-3 h-3 text-cyan-400 -ml-1" />
            <span className="text-[0.9rem] font-bold text-slate-300">
              {t('sensor.indoor_temp_humidity')}
            </span>
          </div>
          <TimeRangeTabs
            value={tempHumidityRange}
            onChange={setTempHumidityRange}
            disabledRanges={!sensor.active ? ['realtime'] : []} />

        </div>

        {/* Current Values */}
        <div className="flex items-center gap-3 mb-2 flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-rose-400" />
            <span className="text-[0.8rem] text-slate-500 uppercase font-bold">
              {t('sensor.temperature')}
            </span>
            <span className="text-base font-roboto font-bold text-rose-400">
              {finalTempHumidData.length > 0 && typeof finalTempHumidData[finalTempHumidData.length - 1].temp === 'number' 
                ? finalTempHumidData[finalTempHumidData.length - 1].temp.toFixed(1) 
                : '--'}°C
            </span>
          </div>
          <div className="h-3.5 w-px bg-white/10" />
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
            <span className="text-[0.8rem] text-slate-500 uppercase font-bold">
              {t('sensor.humidity')}
            </span>
            <span className="text-base font-roboto font-bold text-cyan-400">
              {finalTempHumidData.length > 0 && typeof finalTempHumidData[finalTempHumidData.length - 1].humidity === 'number' 
                ? finalTempHumidData[finalTempHumidData.length - 1].humidity.toFixed(1) 
                : '--'}%
            </span>
          </div>
        </div>

        {/* Combined Chart */}
        <div className="flex-1 min-h-0 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={finalTempHumidData}>
              <defs>
                <linearGradient id="selTempGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#fb7185" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#fb7185" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="selHumidGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" hide />
              <YAxis hide />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#0a0f1a',
                  borderColor: '#334155',
                  fontSize: '0.7rem',
                  borderRadius: '8px'
                }} 
                formatter={(value: number, name: string) => {
                  if (name === 'temp') return [`${Number(value).toFixed(1)}°C`, t('sensor.temperature')];
                  return [`${Number(value).toFixed(1)}%`, t('sensor.humidity')];
                }} />

              <Area
                type="monotone"
                dataKey="temp"
                stroke="#fb7185"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#selTempGrad)" />

              <Area
                type="monotone"
                dataKey="humidity"
                stroke="#22d3ee"
                strokeWidth={1.5}
                fillOpacity={1}
                fill="url(#selHumidGrad)"
                strokeDasharray="4 2" />

            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Averages */}
        <div className="flex justify-between mt-1.5 text-xs flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-0.5 rounded-full bg-rose-400" />
            <span className="text-slate-500 text-[0.7rem]">{t('sensor.average')}</span>
            <span className="font-roboto text-white">
              {finalTempHumidData.length > 0
                ? `${(finalTempHumidData.reduce((sum, item) => sum + (item.temp || 0), 0) / finalTempHumidData.length).toFixed(1)}°C`
                : '--'}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className="w-3 h-0.5 rounded-full bg-cyan-400 opacity-60"
              style={{
                borderTop: '1px dashed #22d3ee'
              }} />

            <span className="text-slate-500 text-[0.7rem]">{t('sensor.average')}</span>
            <span className="font-roboto text-white">
              {finalTempHumidData.length > 0
                ? `${(finalTempHumidData.reduce((sum, item) => sum + (item.humidity || 0), 0) / finalTempHumidData.length).toFixed(1)}%`
                : '--'}
            </span>
          </div>
        </div>
      </motion.div>

      <ChartSection
        icon={GaugeIcon}
        iconColor="text-indigo-400"
        title={t('sensor.pressure')}
        data={finalPressureData}
        timeRange={pressureRange}
        onTimeRangeChange={setPressureRange}
        disabledRanges={!sensor.active ? ['realtime'] : []}
        chartType="line"
        color="#818cf8"
        gradientId="selPressure"
        className="flex-1 min-h-0"
        unit="hPa"
        stats={[
        {
          label: t('sensor.current'),
          value: realtimeData?.['pressure']?.[0]?.value ? ` ${Number(realtimeData?.['pressure']?.[0]?.value/100 || 0).toFixed(1)} hPa` : '- hPa',
          color: 'text-indigo-400'
        },
        {
          label: t('sensor.trend'),
          value: t('sensor.trend_stable')
        }]
        } />

      <AnimatePresence>
        {showLinkPopup && (
          <SwitchConnectPopup
            sensor={sensor}
            onSave={(newSes) => {
              setLinkedSes(newSes);
            }}
            onClose={() => setShowLinkPopup(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
